import React, { useState } from "react";
import { Field, FieldControlProps, FieldLayout } from "./Field";
import "./ui.css";

/**
 * PhoneInput – telefon jako předvolba (select) + číslo, u „Jiná…" navíc pole
 * pro vlastní předvolbu. Sdílí ho registrace (AuthPage) a úprava profilu.
 *
 * Navenek jediná hodnota: celé číslo bez mezer, předvolba a číslo spojené
 * ("+420123456789") – stejný formát, jaký se dosud ukládá do users.phone.
 * Komponenta si hodnotu sama rozloží (známé předvolby → select, jinak
 * „Jiná…") a při změně ji složí zpět a normalizuje: mezery, pomlčky, tečky,
 * lomítka a závorky pryč, "00" na začátku předvolby → "+", chybějící "+"
 * u vlastní předvolby se doplní. Prázdné číslo = "" (i když je vybraná
 * předvolba), takže povinnost pole stačí kontrolovat na prázdný řetězec.
 *
 * Popisek, nápovědu a chybu vykresluje Field (label htmlFor → pole s číslem).
 * Validaci dělá obrazovka – validatePhone() vrací českou hlášku.
 */

/** Předvolby v selectu (vlajky jen v textu option). */
export const PHONE_PREFIXES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "+420", label: "🇨🇿 +420" },
  { value: "+421", label: "🇸🇰 +421" },
  { value: "+36", label: "🇭🇺 +36" },
  { value: "+48", label: "🇵🇱 +48" },
];

const KNOWN_PREFIXES = PHONE_PREFIXES.map((p) => p.value);
const DEFAULT_PREFIX = "+420";
const CUSTOM = "custom";

/**
 * Další běžné mezinárodní předvolby – jen pro rozumné rozdělení uloženého
 * čísla v režimu „Jiná…". Předvolby (E.164) jsou bezprefixové, takže shoda
 * začátku je jednoznačná.
 */
const OTHER_CODES = [
  "+1", "+7", "+30", "+31", "+32", "+33", "+34", "+351", "+353", "+358",
  "+359", "+370", "+371", "+372", "+375", "+380", "+381", "+385", "+386",
  "+39", "+40", "+41", "+43", "+44", "+45", "+46", "+47", "+49",
];

/** Předvolby s pevně devítimístným národním číslem (validace + oprava „420…" v poli čísla). */
const NINE_DIGIT_PREFIXES = ["+420", "+421"];

/** Rozložený stav pole. */
export interface PhoneParts {
  /** Hodnota selectu: známá předvolba ("+420"…) nebo "custom". */
  choice: string;
  /** Vlastní předvolba (jen pro choice "custom"), jak ji uživatel píše. */
  customPrefix: string;
  /** Číslo bez předvolby, jak ho uživatel píše (s mezerami). */
  number: string;
}

/** Odstraní oddělovače: mezery, pomlčky, tečky, lomítka, závorky. */
const stripSeparators = (s: string): string => s.replace(/[\s.()/-]/g, "");

/** Celé číslo bez oddělovačů, "00" na začátku → "+". Ne-text → "". */
export function normalizePhone(raw: unknown): string {
  if (typeof raw !== "string" && typeof raw !== "number") return "";
  let v = stripSeparators(String(raw));
  if (v.indexOf("00") === 0) v = "+" + v.slice(2);
  return v;
}

/** Vlastní předvolba: bez oddělovačů, "00" → "+", chybějící "+" doplní. */
function normalizePrefix(raw: string): string {
  let v = stripSeparators(raw);
  if (v.indexOf("00") === 0) v = "+" + v.slice(2);
  else if (v !== "" && v.charAt(0) !== "+") v = "+" + v;
  return v;
}

/**
 * Rozloží uložené číslo na předvolbu a číslo. Platí composePhone(parsePhone(x))
 * === normalizePhone(x) – nerozpoznaný tvar se nikdy neztratí ani nezmění.
 */
export function parsePhone(value: unknown): PhoneParts {
  const v = normalizePhone(value);
  if (v === "") return { choice: DEFAULT_PREFIX, customPrefix: "", number: "" };

  const known = KNOWN_PREFIXES.find((p) => v.indexOf(p) === 0);
  if (known) {
    return { choice: known, customPrefix: "", number: v.slice(known.length) };
  }

  if (v.charAt(0) === "+") {
    const other = OTHER_CODES.find((p) => v.indexOf(p) === 0);
    if (other) {
      return { choice: CUSTOM, customPrefix: other, number: v.slice(other.length) };
    }
    // Dřívější pravidlo z UpdateProfile: předvolba 1–4 číslice + aspoň 6 číslic.
    const full = /^(\+\d{1,4})(\d{6,})$/.exec(v);
    if (full) return { choice: CUSTOM, customPrefix: full[1], number: full[2] };
    // Krátké / poškozené číslo: rozdělit tak, aby se nic neztratilo.
    const rest = /^(\+\d{0,3})([\s\S]*)$/.exec(v);
    return {
      choice: CUSTOM,
      customPrefix: rest ? rest[1] : v,
      number: rest ? rest[2] : "",
    };
  }

  // Bez předvolby (starší ručně zadané záznamy): předvolbu neznáme – režim
  // „Jiná…" s prázdnou předvolbou; číslo zůstane a nic se tiše nedoplní.
  return { choice: CUSTOM, customPrefix: "", number: v };
}

/** Složí hodnotu pro uložení; prázdné číslo → "". */
export function composePhone(parts: PhoneParts): string {
  const number = stripSeparators(parts.number);
  if (number === "") return "";
  const prefix =
    parts.choice === CUSTOM ? normalizePrefix(parts.customPrefix) : parts.choice;
  return prefix + number;
}

/**
 * Validace telefonu; vrací českou hlášku, nebo undefined, je-li v pořádku.
 * Pravidla: předvolba "+" + 1–4 číslice, číslo aspoň 6 číslic (jako dřív
 * v UpdateProfile), u +420/+421 přesně 9 číslic, celkem nejvýš 15 číslic.
 */
export function validatePhone(
  value: unknown,
  required: boolean = true
): string | undefined {
  const v = normalizePhone(value);
  if (v === "") return required ? "Zadejte telefonní číslo." : undefined;

  const parts = parsePhone(v);
  const prefix = parts.choice === CUSTOM ? parts.customPrefix : parts.choice;
  if (prefix === "" || prefix === "+") return "Doplňte předvolbu (např. +420).";
  if (!/^\+\d{1,4}$/.test(prefix)) {
    return "Předvolba musí mít tvar + a 1–4 číslice (např. +420).";
  }
  if (!/^\d+$/.test(parts.number)) {
    return "Telefonní číslo smí obsahovat jen číslice.";
  }
  if (NINE_DIGIT_PREFIXES.indexOf(prefix) !== -1 && parts.number.length !== 9) {
    return `Číslo s předvolbou ${prefix} musí mít 9 číslic (bez předvolby).`;
  }
  if (parts.number.length < 6) {
    return "Telefonní číslo musí mít alespoň 6 číslic.";
  }
  if (v.length - 1 > 15) return "Telefonní číslo je příliš dlouhé.";
  return undefined;
}

/**
 * Čitelný zápis pro zobrazení (profil): "+420 723 456 789"; devítimístná
 * čísla po trojicích, jiná délka "předvolba číslo". Tvar bez předvolby nebo
 * s jinými znaky vrátí tak, jak je uložený.
 */
export function formatPhone(value: unknown): string {
  const v = normalizePhone(value);
  if (v === "") return "";
  const parts = parsePhone(v);
  const prefix = parts.choice === CUSTOM ? parts.customPrefix : parts.choice;
  if (!/^\+\d{1,4}$/.test(prefix) || !/^\d+$/.test(parts.number)) {
    return typeof value === "string" ? value.trim() : v;
  }
  const number =
    parts.number.length === 9
      ? parts.number.replace(/^(\d{3})(\d{3})(\d{3})$/, "$1 $2 $3")
      : parts.number;
  return `${prefix} ${number}`;
}

/**
 * Celé číslo vložené do pole pro číslo (automatické vyplnění, kopírování,
 * „+420 …", „00420…", u +420/+421 dvanáct číslic začínajících kódem země)
 * → vrátí ho k rozložení; jinak null.
 */
function detectFullNumber(compact: string, choice: string): string | null {
  if (/^(\+|00)\d{7,}$/.test(compact)) return compact;
  if (
    NINE_DIGIT_PREFIXES.indexOf(choice) !== -1 &&
    /^\d{12}$/.test(compact) &&
    compact.indexOf(choice.slice(1)) === 0
  ) {
    return "+" + compact;
  }
  return null;
}

// ─────────────────────────────────────────────────────────────
//  Ovládací prvky (klonuje je Field – dodá id, aria-* a is-invalid)
// ─────────────────────────────────────────────────────────────

interface PhoneControlsProps extends FieldControlProps {
  parts: PhoneParts;
  onPartsChange: (next: PhoneParts) => void;
  disabled?: boolean;
  autoComplete?: string;
}

const PhoneControls: React.FC<PhoneControlsProps> = ({
  id,
  className,
  required,
  "aria-describedby": describedBy,
  "aria-invalid": ariaInvalid,
  parts,
  onPartsChange,
  disabled,
  autoComplete,
}) => {
  const baseId = id || "phone";
  const invalid = !!className && /(^|\s)is-invalid(\s|$)/.test(className);
  const inputClass = (extra: string) =>
    `form-control ${extra}${invalid ? " is-invalid" : ""}`;
  const isCustom = parts.choice === CUSTOM;

  const handleChoice = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onPartsChange({ ...parts, choice: e.target.value });
  };

  const handleCustom = (e: React.ChangeEvent<HTMLInputElement>) => {
    onPartsChange({ ...parts, customPrefix: e.target.value });
  };

  const handleNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const full = detectFullNumber(stripSeparators(raw), parts.choice);
    if (full) {
      // Rozložit, ať nevznikne „+420+420…"; vlastní předvolbu nepřepsat,
      // pokud vložené číslo spadá pod známou předvolbu.
      const parsed = parsePhone(full);
      onPartsChange(
        parsed.choice === CUSTOM
          ? parsed
          : { ...parsed, customPrefix: parts.customPrefix }
      );
      return;
    }
    onPartsChange({ ...parts, number: raw });
  };

  return (
    <div className="lks-phone">
      <select
        id={`${baseId}-prefix`}
        className="form-select lks-phone__prefix"
        aria-label="Předvolba"
        value={parts.choice}
        onChange={handleChoice}
        disabled={disabled}
      >
        {PHONE_PREFIXES.map((p) => (
          <option key={p.value} value={p.value}>
            {p.label}
          </option>
        ))}
        <option value={CUSTOM}>Jiná…</option>
      </select>
      {isCustom && (
        <input
          id={`${baseId}-custom`}
          type="tel"
          inputMode="tel"
          className={inputClass("lks-phone__custom")}
          aria-label="Vlastní předvolba"
          aria-describedby={describedBy}
          aria-invalid={ariaInvalid}
          placeholder="+XXX"
          maxLength={7}
          autoComplete="tel-country-code"
          value={parts.customPrefix}
          onChange={handleCustom}
          required={required}
          disabled={disabled}
        />
      )}
      <input
        id={id}
        type="tel"
        inputMode="tel"
        className={inputClass("lks-phone__number")}
        aria-describedby={describedBy}
        aria-invalid={ariaInvalid}
        placeholder="např. 723 456 789"
        autoComplete={autoComplete}
        value={parts.number}
        onChange={handleNumber}
        required={required}
        disabled={disabled}
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
//  PhoneInput
// ─────────────────────────────────────────────────────────────

export interface PhoneInputProps {
  /** id pole s číslem (label htmlFor); select má `${id}-prefix`, vlastní předvolba `${id}-custom`. */
  id: string;
  /** Celé číslo ("+420123456789"); "" = nevyplněno. Mezery v uložené hodnotě nevadí. */
  value: string;
  /** Každá změna: normalizované celé číslo bez mezer; prázdné číslo = "". */
  onChange: (full: string) => void;
  /** Hvězdička u popisku + required na poli čísla (a vlastní předvolby). */
  required?: boolean;
  /** Chybová hláška (např. z validatePhone) – červený text pod polem, is-invalid. */
  error?: string;
  /** Popisek; výchozí "Telefon". */
  label?: string;
  /** Nápověda pod polem. */
  help?: string;
  disabled?: boolean;
  /** Rozložení Field (stacked / row); výchozí stacked. */
  layout?: FieldLayout;
  className?: string;
  /** autoComplete pole s číslem; výchozí "tel" (vložené celé číslo se samo rozloží). */
  autoComplete?: string;
}

export const PhoneInput: React.FC<PhoneInputProps> = ({
  id,
  value,
  onChange,
  required,
  error,
  label = "Telefon",
  help,
  disabled,
  layout,
  className,
  autoComplete = "tel",
}) => {
  const current = typeof value === "string" ? value : "";

  // Rozpis si komponenta drží sama: rozepsanou vlastní předvolbu ani mezery
  // při psaní nejde z hodnoty zpětně odvodit. `source` = hodnota, ke které
  // rozpis patří; jiná hodnota v props = změna zvenčí (načtení profilu,
  // reset formuláře, rodič změnu nepřijal) → rozpis se převezme z ní.
  const [state, setState] = useState<{ source: string; parts: PhoneParts }>(
    () => ({ source: current, parts: parsePhone(current) })
  );

  let parts = state.parts;
  if (current !== state.source) {
    // Úprava stavu během renderu (doporučený vzor místo efektu): bez
    // bliknutí staré hodnoty. Odpovídá-li hodnota rozpisu, rozpis zůstane.
    parts =
      normalizePhone(current) === composePhone(state.parts)
        ? state.parts
        : parsePhone(current);
    setState({ source: current, parts });
  }

  const handlePartsChange = (next: PhoneParts) => {
    const full = composePhone(next);
    setState({ source: full, parts: next });
    onChange(full);
  };

  return (
    <Field
      id={id}
      label={label}
      required={required}
      help={help}
      error={error}
      layout={layout}
      className={className}
    >
      <PhoneControls
        parts={parts}
        onPartsChange={handlePartsChange}
        disabled={disabled}
        autoComplete={autoComplete}
      />
    </Field>
  );
};

export default PhoneInput;
